import { Button, Typography } from '@cannect/new-components/atoms'
import { useRateConversation } from '@cannect/services/resources/supportChat'
import { motion } from 'framer-motion'
import { ReactNode, useState } from 'react'
import { FiCheck, FiCheckCircle, FiMinusCircle, FiStar, FiXCircle } from 'react-icons/fi'

const SCORES = Array.from({ length: 11 }, (_, index) => index)

/**
 * Cor da nota segundo a régua do CSAT.
 *
 * 0–2 muito insatisfeito, 3–4 insatisfeito, 5 neutro, 6–7 satisfeito, 8–10
 * muito satisfeito. É a mesma faixa que o relatório do gestor usa — colorir
 * por outra faria a tela contar uma história diferente do indicador.
 */
const scoreTone = (score: number, isSelected: boolean) => {
  if (!isSelected) return 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400 hover:bg-neutral-50'
  if (score <= 4) return 'border-red-500 bg-red-500 text-white shadow-md shadow-red-500/20'
  if (score === 5) return 'border-amber-500 bg-amber-500 text-white shadow-md shadow-amber-500/20'
  if (score <= 7) return 'border-lime-600 bg-lime-600 text-white shadow-md shadow-lime-600/20'

  return 'border-primary-700 bg-primary-700 text-white shadow-md shadow-primary-700/20'
}

/** Issue Resolution: a resposta do paciente, que vale 100%, 75% e 0%. */
const RESOLUTIONS: { value: 'total' | 'partial' | 'none'; label: string; icon: ReactNode; selected: string }[] = [
  {
    value: 'total',
    label: 'Sim, totalmente',
    icon: <FiCheckCircle size={18} />,
    selected: 'border-primary-700 bg-primary-50 text-primary-900'
  },
  {
    value: 'partial',
    label: 'Parcialmente',
    icon: <FiMinusCircle size={18} />,
    selected: 'border-amber-500 bg-amber-50 text-amber-900'
  },
  { value: 'none', label: 'Não', icon: <FiXCircle size={18} />, selected: 'border-red-500 bg-red-50 text-red-800' }
]

const EFFORTS = [
  { value: 1, label: 'Muito difícil', face: '😣' },
  { value: 2, label: 'Difícil', face: '🙁' },
  { value: 3, label: 'Neutro', face: '😐' },
  { value: 4, label: 'Fácil', face: '🙂' },
  { value: 5, label: 'Muito fácil', face: '😄' }
]

const Question = ({ children }: { children: ReactNode }) => (
  <Typography.Text type="captionOne" weight="semibold" className="text-neutral-700">
    {children}
  </Typography.Text>
)

type SatisfactionSurveyProps = {
  conversationUuid: string
  /** Nota já registrada — some o formulário e vira agradecimento. */
  answeredScore: number | null
}

const SatisfactionSurvey = ({ conversationUuid, answeredScore }: SatisfactionSurveyProps) => {
  const [score, setScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [resolution, setResolution] = useState<'total' | 'partial' | 'none' | null>(null)
  const [effortScore, setEffortScore] = useState<number | null>(null)
  const [effortComment, setEffortComment] = useState('')

  const rate = useRateConversation()

  const isAnswered = answeredScore !== null || rate.isSuccess

  if (isAnswered) {
    return (
      <motion.section
        aria-label="Avaliação enviada"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center gap-2 rounded-2xl bg-gradient-to-br from-primary-50 to-white px-4 py-6 text-center shadow-sm ring-1 ring-primary-100">
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.1 }}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-700 text-white shadow-md shadow-primary-700/30">
          <FiCheck size={22} aria-hidden />
        </motion.span>
        <Typography.Text type="paragraphTwo" weight="semibold" className="text-primary-900">
          Obrigado pela avaliação!
        </Typography.Text>
        <Typography.Text type="captionOne" className="text-primary-800">
          Sua resposta ajuda a melhorar o atendimento.
        </Typography.Text>
      </motion.section>
    )
  }

  return (
    <section
      aria-label="Pesquisa de satisfação"
      className="flex flex-col gap-4 rounded-2xl bg-white px-4 py-4 shadow-sm ring-1 ring-black/5">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-500">
          <FiStar size={17} />
        </span>
        <div className="flex flex-col gap-0.5">
          <Typography.Text type="paragraphTwo" weight="semibold">
            Quão satisfeito(a) você ficou com o atendimento recebido?
          </Typography.Text>
          <Typography.Text type="captionOne" muted>
            Leva menos de um minuto.
          </Typography.Text>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {/* radiogroup, não botões soltos: leitor de tela anuncia "opção 7 de 11". */}
        <div role="radiogroup" aria-label="Nota de 0 a 10" className="grid grid-cols-6 gap-1.5 sm:grid-cols-11">
          {SCORES.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={score === value}
              aria-label={`Nota ${value}`}
              onClick={() => setScore(value)}
              className={`flex h-9 items-center justify-center rounded-lg border border-solid text-xs font-semibold transition ${scoreTone(
                value,
                score === value
              )}`}>
              {value}
            </button>
          ))}
        </div>

        {/* A régua de cor diz o sentido da escala antes de ler as pontas. */}
        <span aria-hidden className="h-1 rounded-full bg-gradient-to-r from-red-400 via-amber-400 to-primary-600" />

        <div className="flex items-center justify-between px-0.5">
          <Typography.Text type="captionOne" muted>
            Muito insatisfeito
          </Typography.Text>
          <Typography.Text type="captionOne" muted>
            Muito satisfeito
          </Typography.Text>
        </div>
      </div>

      {score !== null && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col gap-4">
          <label htmlFor="nps-comment" className="flex flex-col gap-1.5">
            <Question>Quer contar o motivo? (opcional)</Question>
            <textarea
              id="nps-comment"
              rows={2}
              maxLength={500}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="O que poderia ter sido melhor?"
              className="resize-none rounded-xl border border-solid border-neutral-200 bg-neutral-50 px-3 py-2 font-sans text-sm outline-none transition focus:border-primary-700 focus:bg-white"
            />
          </label>

          {/*
            Issue Resolution. Pergunta separada da satisfação de propósito: dá
            para sair satisfeito com o atendimento e com o problema em aberto,
            e misturar as duas apagaria justamente esse caso.
          */}
          <div className="flex flex-col gap-1.5">
            <Question>Seu problema foi resolvido durante este atendimento?</Question>
            <div role="radiogroup" aria-label="Resolução do problema" className="grid grid-cols-3 gap-1.5">
              {RESOLUTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={resolution === option.value}
                  onClick={() => setResolution(option.value)}
                  className={`flex flex-col items-center gap-1 rounded-xl border border-solid px-2 py-2.5 text-[11px] font-semibold transition ${
                    resolution === option.value
                      ? option.selected
                      : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400'
                  }`}>
                  {option.icon}
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Customer Effort Score: o esforço que ele teve para ser atendido. */}
          <div className="flex flex-col gap-1.5">
            <Question>Foi fácil conseguir a ajuda que você precisava?</Question>
            <div role="radiogroup" aria-label="Facilidade do atendimento" className="grid grid-cols-5 gap-1.5">
              {EFFORTS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={effortScore === option.value}
                  aria-label={option.label}
                  title={option.label}
                  onClick={() => setEffortScore(option.value)}
                  className={`flex flex-col items-center gap-0.5 rounded-xl border border-solid px-1 py-2 transition ${
                    effortScore === option.value
                      ? 'border-primary-700 bg-primary-50'
                      : 'border-neutral-200 bg-white hover:border-neutral-400'
                  }`}>
                  <span
                    aria-hidden
                    className={`text-xl leading-none transition ${
                      effortScore === null || effortScore === option.value ? '' : 'opacity-40 grayscale'
                    }`}>
                    {option.face}
                  </span>
                  <span className="text-center text-[9px] font-medium leading-tight text-neutral-600">
                    {option.label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {effortScore !== null && (
            <label htmlFor="effort-comment" className="flex flex-col gap-1.5">
              <Question>Quer contar o que dificultou ou facilitou? (opcional)</Question>
              <textarea
                id="effort-comment"
                rows={2}
                maxLength={500}
                value={effortComment}
                onChange={(event) => setEffortComment(event.target.value)}
                placeholder="O que fez diferença para você?"
                className="resize-none rounded-xl border border-solid border-neutral-200 bg-neutral-50 px-3 py-2 font-sans text-sm outline-none transition focus:border-primary-700 focus:bg-white"
              />
            </label>
          )}

          {/*
            Só a satisfação trava o envio. Exigir as três faria quem respondeu
            a primeira e desistiu não contar para nada — metade da pesquisa vale
            mais que nenhuma.
          */}
          <Button
            size="md"
            full
            disabled={rate.isPending}
            onClick={() =>
              rate.mutate({
                uuid: conversationUuid,
                score,
                comment: comment.trim() || undefined,
                resolution: resolution ?? undefined,
                effortScore: effortScore ?? undefined,
                effortComment: effortComment.trim() || undefined
              })
            }>
            {rate.isPending ? 'Enviando…' : 'Enviar avaliação'}
          </Button>
        </motion.div>
      )}

      {rate.isError && (
        <Typography.Text type="captionOne" className="text-center text-red-600">
          Não conseguimos registrar sua avaliação. Tente de novo.
        </Typography.Text>
      )}
    </section>
  )
}

export default SatisfactionSurvey
